import Browser from "../browser";

interface InstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: InstallPromptEvent | undefined;
let installed = false;
let started = false;
let snapshot = { available: false, installed: false };
const listeners = new Set<() => void>();

export function isAppleMobile(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

function standalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches
    || window.matchMedia("(display-mode: fullscreen)").matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function publish() {
  const nextInstalled = installed || standalone();
  snapshot = { available: !!deferredPrompt && !nextInstalled, installed: nextInstalled };
  for (const listener of listeners) listener();
}

/** Start at application boot so prompts arriving before a dialog mounts are retained. */
export function startPwaInstallCapture() {
  if (started) return;
  started = true;
  window.addEventListener("beforeinstallprompt", event => {
    const prompt = event as InstallPromptEvent;
    if (typeof prompt.prompt !== "function") return;
    if (Browser.isMobile() || isAppleMobile()) event.preventDefault();
    deferredPrompt = prompt;
    publish();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferredPrompt = undefined;
    publish();
  });
  window.matchMedia("(display-mode: standalone)").addEventListener("change", publish);
  publish();
}

export function subscribePwaInstall(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export const pwaInstallSnapshot = () => snapshot;

/** A browser prompt can be used once and must be invoked directly from a click. */
export async function requestPwaInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const prompt = deferredPrompt;
  if (!prompt || snapshot.installed) return "unavailable";
  deferredPrompt = undefined;
  publish();
  const choice = await prompt.prompt();
  return choice.outcome;
}
